import { useCallback, useEffect, useState } from 'react'
import {
  fetchCustomer,
  resetSignUp,
  selectBrand,
  selectContent,
  selectRecaptcha,
  selectSignUp,
  signUpCustomer,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  CheckoutConfig,
  Customer,
  CustomerCreate,
  CustomerNotificationPrefsCreate,
  FormFieldType,
} from '@open-tender/types'

import {
  ButtonSubmit,
  FormError,
  FormInputs,
  FormSubmit,
  GuestCommunicationPreferences,
  ThanxTerms,
  ThirdPartyLoyaltyTerms,
  HomeStoreSelector,
} from 'components'
import Body from 'components/Body'
import PasswordRequirements from 'components/PasswordRequirements'
import styled from '@emotion/styled'
import { sendSignup } from 'app/analytics'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { makeNotificationPrefs } from 'utils/commsPrefs'
import { usePasswordValidation, useSignUpForm } from '@open-tender/utils'

const SignUpFormCommunications = styled.div`
  label {
    margin: 0 0 1.5rem;
  }
`

const SignUpForm = ({
  windowRef,
  callback,
  checkConfig,
}: {
  windowRef?: React.RefObject<HTMLDivElement>
  callback?: (data: Customer) => void
  checkConfig?: CheckoutConfig
}) => {
  const dispatch = useAppDispatch()
  const {
    has_thanx = false,
    tpls,
    notification_preferences: prefs,
  } = useAppSelector(selectBrand) || {}
  const { loading, error } = useAppSelector(selectSignUp)
  const { signUp: config } = useAppSelector(selectContent) || {}
  const { accepts_marketing, order_notifications, notification_preferences } =
    useAppSelector(selectBrand) || {}
  const { signup: includeRecaptcha, login: includeLoginRecaptcha } =
    useAppSelector(selectRecaptcha) || {}
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [communicationPrefs, setCommunicationPrefs] =
    useState<CustomerNotificationPrefsCreate>([])

  const {
    handlePasswordChange,
    isPasswordValid,
    shouldShowRequirements,
    getPasswordErrors,
  } = usePasswordValidation()

  const getRecaptchaToken = async () =>
    (await executeRecaptcha?.('signup')) || null
  const getLoginRecaptchaToken = async () =>
    (await executeRecaptcha?.('login')) || null

  const optionalFields = config?.displayed || []
  const hidePassword = has_thanx || tpls === 'COMO'
  const notificationChannels = prefs
    ? Array.from(new Set(prefs?.map((i) => i.notification_channel)))
    : []

  const notificationPrefsLookup = makeNotificationPrefs(
    notification_preferences
  )

  const passwordRequirements =
    tpls === 'PUNCHH' ? ' (must be at least 10 characters)' : ''
  const { display_marketing_message, marketing } = config || {}
  const commsMessage = display_marketing_message
    ? marketing || 'Choose your communication preferences:'
    : null

  const hasThirdPartyTerms = !!tpls && tpls !== 'SPARKFLY'

  const signUp = useCallback(
    (
      data: CustomerCreate,
      callback?: (data: Customer) => void,
      token?: string,
      loginToken?: string
    ) => {
      const augmented = {
        ...data,
        customer_notification_preferences: communicationPrefs,
        token,
        login_recaptcha: loginToken,
      }
      dispatch(
        signUpCustomer({
          data: augmented,
          callback,
        })
      )
    },
    [dispatch, communicationPrefs]
  )

  const {
    submitRef,
    data,
    errors,
    submitting,
    fields,
    handleChange,
    handleSubmit,
  } = useSignUpForm(
    loading,
    error,
    signUp,
    callback,
    checkConfig,
    hidePassword,
    optionalFields,
    notificationChannels,
    passwordRequirements,
    tpls,
    includeRecaptcha,
    getRecaptchaToken,
    includeLoginRecaptcha,
    getLoginRecaptchaToken,
    config?.require_home_store
  )

  const errMsg = errors.form?.includes('parameters')
    ? 'There are one or more errors below. Please review and resubmit.'
    : errors.form

  // Client-side password validation
  // Note: We don't set errors.password here because the PasswordRequirements
  // component provides better visual feedback with checkmarks.
  // The validation still blocks form submission via handleFormSubmit.

  const onChange = (field: FormFieldType, value: string | number | boolean) => {
    handleChange(field.name, value)

    if (field.name === 'password') {
      handlePasswordChange()
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!hidePassword && data.password) {
      handlePasswordChange()
    }

    // Prevent submission if password validation fails
    const passwordValid =
      hidePassword || !data.password || isPasswordValid(data.password as string)
    if (!passwordValid) {
      return
    }

    handleSubmit(e)
  }

  useEffect(() => {
    dispatch(resetSignUp())
    return () => {
      dispatch(resetSignUp())
      dispatch(fetchCustomer()).then((d) => {
        if (d.type.endsWith('fulfilled')) sendSignup(data.email)
      })
    }
  }, [data.email, dispatch])

  useEffect(() => {
    if (error) {
      if (windowRef?.current) {
        windowRef.current.scrollTop = 0
      } else {
        window.scrollTo(0, 0)
      }
    }
  }, [error, windowRef])

  // Separate password field from other fields for custom rendering
  const passwordField = fields.find((f) => f.name === 'password')
  const otherFields = fields.filter((f) => f.name !== 'password')
  const showPasswordRequirements = shouldShowRequirements(
    data.password as string,
    hidePassword
  )
  const passwordErrors = getPasswordErrors(
    data.password as string,
    errors,
    'password'
  )

  return (
    <form id="signup-form" onSubmit={handleFormSubmit} noValidate>
      {has_thanx && <ThanxTerms />}
      <FormError errMsg={errMsg} style={{ margin: '0 0 2rem' }} />
      <FormInputs
        fields={otherFields}
        data={data}
        onChange={onChange}
        errors={errors}
      />
      {passwordField && !hidePassword && (
        <div>
          <FormInputs
            fields={[passwordField]}
            data={data}
            onChange={(field, value) => {
              onChange(field, value)
            }}
            errors={passwordErrors} // Show backend errors, PasswordRequirements shows frontend validation
          />
          {showPasswordRequirements && (
            <PasswordRequirements password={(data.password as string) || ''} />
          )}
        </div>
      )}
      {config?.require_home_store && (
        <HomeStoreSelector
          initialHomeStore={data.home_store}
          onHomeStoreChange={(value: string) =>
            handleChange('home_store', value)
          }
          error={errors.home_store}
        />
      )}
      <SignUpFormCommunications>
        {commsMessage && (
          <Body as="p" size="small" style={{ marginBottom: 15 }}>
            {commsMessage}
          </Body>
        )}
        <GuestCommunicationPreferences
          brandPrefs={notificationPrefsLookup}
          communicationPrefs={communicationPrefs}
          setCommunicationPrefs={setCommunicationPrefs}
          accepts_marketing={accepts_marketing}
          order_notifications={order_notifications}
        />
      </SignUpFormCommunications>
      {hasThirdPartyTerms && <ThirdPartyLoyaltyTerms />}
      <FormSubmit>
        <ButtonSubmit submitRef={submitRef} submitting={submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </ButtonSubmit>
      </FormSubmit>
    </form>
  )
}

export default SignUpForm
